import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Bell, CalendarDays, CalendarPlus, Clock, Loader2, MapPin, Minus, Plus, Ticket, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import SiteNav from "@/components/landing/Nav";
import DynamicForm from "@/components/forms/DynamicForm";
import ShareButtons from "@/components/ShareButtons";
import { useCart } from "@/context/CartContext";
import { downloadIcs, googleCalUrl } from "@/lib/calendar";
import { api, formatApiError } from "@/lib/api";
import { formatTimeRange12 } from "@/lib/time";
import { seoDesc, metaDescription } from "@/lib/seo";

const EASE = [0.16, 1, 0.3, 1];

function QuickRegister({ event }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", attendees: 1 });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/events/${event.id}/register`, { ...form, attendees: Number(form.attendees) });
      setDone(true);
      toast.success("You're registered! See you there.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="py-6 text-center" data-testid="quick-register-success">
        <p className="font-display text-xl font-semibold text-gold">You're in!</p>
        <p className="mt-2 text-sm text-muted-foreground">Your spot is confirmed — details will follow by email.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="quick-register-form">
      <div>
        <Label htmlFor="qr-name">Full name</Label>
        <Input id="qr-name" data-testid="quick-register-name" required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
      </div>
      <div>
        <Label htmlFor="qr-email">Email</Label>
        <Input id="qr-email" data-testid="quick-register-email" type="email" required value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="qr-phone">Phone *</Label>
          <Input id="qr-phone" data-testid="quick-register-phone" type="tel" required value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
        </div>
        <div>
          <Label htmlFor="qr-attendees">Attendees</Label>
          <Input id="qr-attendees" data-testid="quick-register-attendees" type="number" min="1" max="10" required value={form.attendees}
            onChange={(e) => setForm({ ...form, attendees: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
        </div>
      </div>
      <Button data-testid="quick-register-submit" type="submit" disabled={loading}
        className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reserve My Free Spot"}
      </Button>
    </form>
  );
}

// Email capture form — used for "registration opens" and "deadline approaching" reminders.
function ReminderForm({ event, testId }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/events/${event.id}/remind`, { email });
      setDone(true);
      toast.success(data.already ? "You're already on the list — we'll email you." : "You're on the list! We'll keep you posted by email.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p data-testid={`${testId}-success`} className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm font-medium text-emerald-300">
        You're on the list — watch your inbox.
      </p>
    );
  }
  return (
    <form data-testid={`${testId}-form`} onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
      <Input data-testid={`${testId}-email-input`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com" className="h-11 flex-1 border-gold/25 bg-ink" />
      <Button data-testid={`${testId}-submit-button`} type="submit" disabled={loading}
        className="h-11 rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remind Me"}
      </Button>
    </form>
  );
}

// Shown when the event's registration window hasn't opened yet or has closed.
function RegistrationWindowNotice({ event, state }) {
  const notYet = state === "not_yet";
  const openDate = event.registration_opens
    ? new Date(`${event.registration_opens}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "soon";
  const closeDate = event.registration_closes
    ? new Date(`${event.registration_closes}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className="mt-9 rounded-3xl border border-gold/25 bg-ink-card/70 p-7 sm:p-8" data-testid="registration-window-notice">
      <div className="flex items-center gap-2.5">
        <Bell className="h-5 w-5 text-gold" />
        <h2 className="font-display text-xl font-semibold">
          {notYet ? `Registration opens ${openDate}` : "Registration closed"}
        </h2>
      </div>
      {notYet ? (
        <>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Mark your calendar — registration for this event opens <span className="text-gold">{openDate}</span>.
            Drop your email below and we'll let you know the moment it opens.
          </p>
          <ReminderForm event={event} testId="reminder" />
        </>
      ) : (
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Registration for this event {closeDate ? `closed on ${closeDate}` : "has closed"}. Browse the details above,
          and join us at an upcoming event — new dates are added regularly.
        </p>
      )}
    </div>
  );
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regForm, setRegForm] = useState(undefined);
  const [qty, setQty] = useState(1);
  const [regDone, setRegDone] = useState(false);
  const [tierQty, setTierQty] = useState({});

  useEffect(() => {
    setLoading(true);
    setRegDone(false);
    setTierQty({});
    api.get(`/events/${eventId}`)
      .then(({ data }) => {
        setEvent(data);
        if (Number(data.price) <= 0 && data.form_id) {
          api.get(`/forms/${data.form_id}`).then(({ data: f }) => setRegForm(f)).catch(() => setRegForm(null));
        } else {
          setRegForm(null);
        }
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  const tiers = event?.variations || [];
  const hasTiers = tiers.length > 0;
  const isFree = event && Number(event.price) <= 0 && !tiers.some((t) => Number(t.price) > 0);
  const isPast = !!(event && event.past);
  const isSoldOut = !isPast && !!event?.sold_out;
  const tierCount = tiers.reduce((s, t) => s + (tierQty[t.id] || 0), 0);
  const tierTotal = tiers.reduce((s, t) => s + (tierQty[t.id] || 0) * Number(t.price), 0);
  const minTier = hasTiers ? Math.min(...tiers.map((t) => Number(t.price))) : null;
  const anyPaidTier = tiers.some((t) => Number(t.price) > 0);
  const priceBadge = hasTiers
    ? (anyPaidTier ? (minTier <= 0 ? "Free & paid options" : `From $${minTier} / attendee`) : "Free Event")
    : (isFree ? "Free Event" : `$${Number(event?.price || 0).toFixed(0)} / attendee`);

  const enroll = () => {
    if (hasTiers) {
      const chosen = tiers.filter((t) => (tierQty[t.id] || 0) > 0);
      if (!chosen.length) {
        toast.error("Pick at least one ticket first.");
        return;
      }
      chosen.forEach((t) =>
        cart.add(
          {
            type: "event", ref_id: event.id, name: event.title,
            variation_id: t.id, variation_name: t.name,
            unit_price: Number(t.price), image: event.image,
            quantity: tierQty[t.id],
          },
          { openDrawer: false }
        )
      );
    } else {
      cart.add(
        {
          type: "event", ref_id: event.id, name: event.title,
          variation_id: null, variation_name: null,
          unit_price: Number(event.price), image: event.image,
          quantity: qty,
        },
        { openDrawer: false }
      );
    }
    navigate("/checkout");
  };

  const eventJsonLd = event
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.title,
        description: metaDescription(seoDesc(event), 200),
        ...(event.start_date && {
          startDate: event.start_time ? `${event.start_date}T${event.start_time}` : event.start_date,
        }),
        ...(event.start_date && event.end_time && { endDate: `${event.start_date}T${event.end_time}` }),
        eventStatus: "https://schema.org/EventScheduled",
        ...(event.location && { location: { "@type": "Place", name: event.location } }),
        ...(event.image && { image: [new URL(event.image, window.location.origin).href] }),
        organizer: { "@type": "Organization", name: "AB Hunter Consulting USA", url: window.location.origin },
        ...(!isFree && {
          offers: (hasTiers ? tiers : [{ name: "General Admission", price: event.price }]).map((t) => ({
            "@type": "Offer",
            name: t.name,
            price: Number(t.price).toFixed(2),
            priceCurrency: "USD",
            availability: isSoldOut ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
            url: `${window.location.origin}/events/${event.id}`,
          })),
        }),
      }
    : undefined;

  return (
    <div data-testid="event-detail-page" className="min-h-screen bg-ink text-foreground">
      <Seo
        title={event ? `${event.title} — AB Hunter Consulting USA` : "Event — AB Hunter Consulting USA"}
        siteName="AB Hunter Consulting USA"
        description={event ? seoDesc(event) : "AB Hunter Consulting USA event."}
        image={event ? event.image || "/assets/academy-1.png" : undefined}
        jsonLd={eventJsonLd}
      />
      <SiteNav />
      <div className="lg:pl-64">
        <main className="hero-glow grain relative mx-auto max-w-6xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
          <Link to="/" data-testid="event-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Back to events
          </Link>

          {loading ? (
            <div className="flex justify-center py-32" data-testid="event-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
          ) : !event ? (
            <div className="py-32 text-center" data-testid="event-not-found">
              <p className="font-display text-2xl">Event not found</p>
              <Button asChild className="mt-6 rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover"><Link to="/">Return home</Link></Button>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
                <div className="relative">
                  <div className="absolute -inset-3 rounded-[2rem] border border-gold/15" />
                  <div className="relative overflow-hidden rounded-[1.75rem] border border-gold/25 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]">
                    <img src={event.image || "/assets/academy-1.png"} alt={event.title} className={`aspect-[4/3] w-full object-cover ${isPast ? "grayscale" : ""}`} />
                    <span data-testid="event-detail-badge" className={`absolute left-5 top-5 rounded-full px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.15em] ${isPast ? "bg-zinc-600/90 text-white" : isSoldOut ? "bg-red-500/90 text-white" : isFree ? "bg-emerald-400/90 text-ink" : "bg-gold text-ink"}`}>
                      {isPast ? "Past Event" : isSoldOut ? "Sold Out" : priceBadge}
                    </span>
                  </div>
                </div>

                {!isPast && (
                <div className="mt-8 rounded-2xl border border-gold/15 bg-ink-card/60 p-6" data-testid="add-to-calendar-box">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Add to your calendar</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button data-testid="add-to-calendar-ics" variant="outline" onClick={() => { downloadIcs(event); toast.success("Calendar file downloaded — open it to save the event."); }}
                      className="rounded-full border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                      <CalendarPlus className="mr-2 h-4 w-4" /> Download .ics (Apple / Outlook / Android)
                    </Button>
                    <Button data-testid="add-to-calendar-google" variant="outline" asChild
                      className="rounded-full border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                      <a href={googleCalUrl(event)} target="_blank" rel="noopener noreferrer">
                        <CalendarDays className="mr-2 h-4 w-4" /> Google Calendar
                      </a>
                    </Button>
                  </div>
                </div>
                )}

                <div className="mt-8 rounded-2xl border border-gold/15 bg-ink-card/60 p-6" data-testid="share-event-box">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Spread the word</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Know someone who should be in the room? Share this event with them.</p>
                  <div className="mt-4">
                    <ShareButtons
                      testId="event-share"
                      title={event.title}
                      text={[event.date_label, event.location].filter(Boolean).join(" · ")}
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.12, ease: EASE }}>
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{event.category}{event.event_type ? ` · ${event.event_type}` : ""}</span>
                <h1 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{event.title}</h1>

                <div className="mt-6 space-y-3 text-sm text-foreground/85">
                  <p className="flex items-center gap-3"><CalendarDays className="h-4 w-4 shrink-0 text-gold" /> {event.date_label}</p>
                  {(event.start_time || event.end_time) && (
                    <p className="flex items-center gap-3"><Clock className="h-4 w-4 shrink-0 text-gold" /> {formatTimeRange12(event.start_time, event.end_time)}</p>
                  )}
                  <p className="flex items-center gap-3"><MapPin className="h-4 w-4 shrink-0 text-gold" /> {event.location}</p>
                  <p className="flex items-center gap-3"><Users className="h-4 w-4 shrink-0 text-gold" />
                    <span data-testid="event-detail-spots">
                      {event.show_spots && !isPast
                        ? (isSoldOut ? "Sold out — no spots left" : `${event.spots_remaining} of ${event.capacity} spots left`)
                        : `${event.capacity} spots`}
                    </span>
                  </p>
                </div>

                <p className="mt-6 whitespace-pre-line leading-relaxed text-muted-foreground">{event.description}</p>

                {isPast || isSoldOut ? (
                  <div className="mt-9 rounded-3xl border border-gold/15 bg-ink-card/50 p-7 sm:p-8" data-testid={isPast ? "event-past-notice" : "event-soldout-notice"}>
                    <div className="flex items-center gap-2.5">
                      <Ticket className={`h-5 w-5 ${isSoldOut ? "text-red-400" : "text-muted-foreground"}`} />
                      <h2 className="font-display text-xl font-semibold">{isPast ? "This event has passed" : "Sold out"}</h2>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {isPast
                        ? "Registration and enrollment are closed — this page is for information only. Browse the details above, and join us at an upcoming event."
                        : "This event is at full capacity and enrollment is closed. Take a look at our other upcoming events — new dates are added regularly."}
                    </p>
                    <Button asChild data-testid="past-event-upcoming-link"
                      className="mt-5 rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">
                      <Link to="/#events">See Upcoming Events <ArrowRight className="ml-2 h-4 w-4" /></Link>
                    </Button>
                  </div>
                ) : (event.registration_state && event.registration_state !== "open") ? (
                  <RegistrationWindowNotice event={event} state={event.registration_state} />
                ) : (
                <div className="mt-9 rounded-3xl border border-gold/25 bg-ink-card/70 p-7 sm:p-8" data-testid="event-enroll-card">
                  {isFree ? (
                    <>
                      <div className="flex items-center gap-2.5">
                        <Ticket className="h-5 w-5 text-gold" />
                        <h2 className="font-display text-xl font-semibold">Register — it's free</h2>
                      </div>
                      <div className="mt-5">
                        {event.form_id ? (
                          regForm === undefined ? (
                            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
                          ) : regForm ? (
                            regDone ? (
                              <p className="py-6 text-center font-display text-xl font-semibold text-gold" data-testid="event-reg-success">{regForm.success_message}</p>
                            ) : (
                              <DynamicForm form={regForm} eventId={event.id} testIdPrefix="event-register"
                                onSuccess={() => { setRegDone(true); toast.success("You're registered! See you there."); }} />
                            )
                          ) : (
                            <QuickRegister event={event} />
                          )
                        ) : (
                          <QuickRegister event={event} />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2.5">
                        <Ticket className="h-5 w-5 text-gold" />
                        <h2 className="font-display text-xl font-semibold">
                          {hasTiers ? "Choose your tickets" : `Enroll — $${Number(event.price).toFixed(0)} per attendee`}
                        </h2>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {hasTiers
                          ? "Pick how many of each ticket you need — mix and match. You'll name each attendee at checkout."
                          : "Secure checkout right here on the site. You'll name each attendee at checkout."}
                      </p>
                      {hasTiers && (
                        <div className="mt-5 space-y-2.5" data-testid="tier-picker">
                          {tiers.map((t) => (
                            <div key={t.id} data-testid={`tier-option-${t.id}`}
                              className={`flex items-center gap-4 rounded-xl border p-4 transition-all ${
                                (tierQty[t.id] || 0) > 0 ? "border-gold/60 bg-gold/10" : "border-gold/15 bg-ink"
                              }`}>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold leading-snug">{t.name}</p>
                                {t.description && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t.description}</p>}
                                <p className={`mt-1 font-display text-sm font-semibold ${Number(t.price) <= 0 ? "text-emerald-300" : "text-gold"}`}>
                                  {Number(t.price) <= 0 ? "Free" : `$${Number(t.price).toFixed(0)} each`}
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2.5">
                                <button type="button" data-testid={`tier-minus-${t.id}`} aria-label={`Fewer ${t.name} tickets`}
                                  onClick={() => setTierQty((prev) => ({ ...prev, [t.id]: Math.max(0, (prev[t.id] || 0) - 1) }))}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold transition-colors hover:bg-gold/10">
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span data-testid={`tier-qty-${t.id}`} className="w-6 text-center font-display text-lg font-semibold">{tierQty[t.id] || 0}</span>
                                <button type="button" data-testid={`tier-plus-${t.id}`} aria-label={`More ${t.name} tickets`}
                                  onClick={() => setTierQty((prev) => ({ ...prev, [t.id]: Math.min(10, (prev[t.id] || 0) + 1) }))}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold transition-colors hover:bg-gold/10">
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {!hasTiers && (
                      <div className="mt-5 flex items-center gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Attendees</span>
                        <button data-testid="enroll-qty-minus" aria-label="Decrease attendees" onClick={() => setQty(Math.max(1, qty - 1))}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold hover:bg-gold/10">
                          <Minus className="h-4 w-4" />
                        </button>
                        <span data-testid="enroll-qty" className="w-8 text-center font-display text-lg font-semibold">{qty}</span>
                        <button data-testid="enroll-qty-plus" aria-label="Increase attendees" onClick={() => setQty(Math.min(10, qty + 1))}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold hover:bg-gold/10">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      )}
                      <Button data-testid="event-enroll-button" onClick={enroll}
                        className="mt-5 w-full rounded-full bg-gold py-6 text-base font-semibold text-ink transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]">
                        {hasTiers
                          ? tierCount === 0
                            ? "Select your tickets"
                            : tierTotal <= 0
                              ? `Enroll Now — Free (${tierCount} ticket${tierCount > 1 ? "s" : ""})`
                              : `Enroll Now — $${tierTotal.toFixed(2)} (${tierCount} ticket${tierCount > 1 ? "s" : ""})`
                          : Number(event.price) <= 0
                            ? "Enroll Now — Free"
                            : `Enroll Now — $${(Number(event.price) * qty).toFixed(2)}`} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
                )}
                {!isPast && !isSoldOut && event.registration_state === "open" && event.registration_closes && (
                  <div className="mt-6 rounded-2xl border border-gold/15 bg-ink-card/50 p-6" data-testid="deadline-reminder-box">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                      <Bell className="h-3.5 w-3.5" />
                      Registration closes {new Date(`${event.registration_closes}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                      Not ready yet? Leave your email and we'll nudge you a week before the deadline and again with 24 hours left.
                    </p>
                    <ReminderForm event={event} testId="deadline-reminder" />
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
